import { emptyBuckets } from './costs.js';
import { MONTH_LABELS } from './dates.js';
import { tcoMaandenVoorAuto, TCO_POSTEN } from './tco.js';
function leegPosten() {
    return {
        afschrijving: 0,
        lease: 0,
        verzekering: 0,
        wegenbelasting: 0,
        vastOverig: 0,
        onderhoud: 0,
        variabel: 0,
    };
}
const VASTE_POSTEN = TCO_POSTEN.filter((p) => p.vast).map((p) => p.key);
function isVast(post) {
    return VASTE_POSTEN.includes(post);
}
/**
 * The calendar years the *measured* data covers.
 *
 * From the cost lines and nothing else, deliberately. The road-tax history goes
 * back to 2022 while the invoices start in July 2025, so deriving the years from
 * the rates would offer four years that consist purely of extrapolated fixed
 * costs with no measured euro in them. That is the same reason `startMaand` in
 * `tco.ts` takes the later of the two.
 */
export function vlootJaren(autos, now = new Date()) {
    const jaren = new Set();
    for (const a of autos) {
        for (const r of a.regels) {
            if (r.maand)
                jaren.add(Number(r.maand.slice(0, 4)));
        }
    }
    if (!jaren.size)
        jaren.add(now.getFullYear());
    return [...jaren].sort((a, b) => a - b);
}
/**
 * The TCO per car over an arbitrary run of months, dearest first.
 *
 * Separate from `buildVlootTco` because the two answer to different periods. The
 * chart needs a fixed twelve-month axis to be readable, while the ranking follows
 * whatever period the reader picked — asking "which car cost me most in March"
 * is a normal question and the chart cannot answer it.
 *
 * Cars that cost nothing in the window drop out rather than sitting at zero.
 */
export function vlootTcoPerAuto({ autos, maanden, now = new Date(), }) {
    if (!maanden.length)
        return [];
    const gesorteerd = [...maanden].sort();
    const van = gesorteerd[0];
    const tot = gesorteerd[gesorteerd.length - 1];
    const inScope = new Set(maanden);
    const uit = [];
    for (const a of autos) {
        const perMaand = tcoMaandenVoorAuto({
            car: a.car,
            regels: a.regels,
            tarieven: a.tarieven,
            van,
            tot,
            now,
        });
        const posten = leegPosten();
        for (const [maand, m] of perMaand) {
            // Een periode hoeft niet aaneengesloten te zijn, dus per maand toetsen in
            // plaats van op de grenzen vertrouwen.
            if (!inScope.has(maand))
                continue;
            for (const k of Object.keys(posten))
                posten[k] += m.posten[k];
        }
        let vast = 0;
        let variabel = 0;
        for (const k of Object.keys(posten)) {
            if (isVast(k))
                vast += posten[k];
            else
                variabel += posten[k];
        }
        const totaal = vast + variabel;
        if (totaal === 0)
            continue;
        uit.push({
            kenteken: a.car.kenteken,
            auto: `${a.car.merk} ${a.car.model}`.trim(),
            bestuurder: a.car.bestuurderNaam || '\u2014',
            brandstof: a.car.brandstof,
            eigendom: a.car.eigendom,
            posten,
            vast,
            variabel,
            totaal,
        });
    }
    return uit.sort((a, b) => b.totaal - a.totaal);
}
/** The twelve months of one calendar year, empty. */
function legeMaanden(jaar, nuIdx) {
    return Array.from({ length: 12 }, (_, i) => ({
        key: `${jaar}-${String(i + 1).padStart(2, '0')}`,
        label: MONTH_LABELS[i] ?? '',
        posten: leegPosten(),
        variabelPerBucket: emptyBuckets(),
        totaal: 0,
        autos: 0,
        future: jaar * 12 + i > nuIdx,
    }));
}
/** Fill in the running totals a bar carries. */
function sluitAf(bar) {
    bar.totaal = Object.keys(bar.posten).reduce((s, k) => s + bar.posten[k], 0);
    return bar;
}
/**
 * Price the whole fleet, as every view the chart can show.
 *
 * Per calendar year that is twelve monthly bars, including the ones still ahead:
 * the axis then matches the monthly cost stack elsewhere on the page, and a
 * running year's empty tail is exactly the filler the car page already decided it
 * wanted. On top of that comes one bar per year, for reading several years
 * against each other without a dozen bars per year in the way.
 *
 * Archived cars are included, bounded by their own last cost line the way
 * `buildTco` bounds them. Leaving them out would make this chart disagree with
 * the spend chart by the cost of a sold car, with nothing on screen to explain
 * the gap.
 */
export function buildVlootTco({ autos, now = new Date(), }) {
    const nuIdx = now.getFullYear() * 12 + now.getMonth();
    const jaren = vlootJaren(autos, now);
    const perJaar = new Map(jaren.map((j) => [j, legeMaanden(j, nuIdx)]));
    const van = `${jaren[0]}-01`;
    const tot = `${jaren[jaren.length - 1]}-12`;
    for (const a of autos) {
        const maanden = tcoMaandenVoorAuto({
            car: a.car,
            regels: a.regels,
            tarieven: a.tarieven,
            van,
            tot,
            now,
        });
        for (const [maand, m] of maanden) {
            const bars = perJaar.get(Number(maand.slice(0, 4)));
            if (!bars)
                continue;
            const bar = bars[Number(maand.slice(5, 7)) - 1];
            if (m.actief)
                bar.autos++;
            for (const k of Object.keys(bar.posten))
                bar.posten[k] += m.posten[k];
            for (const k of Object.keys(bar.variabelPerBucket)) {
                bar.variabelPerBucket[k] += m.variabelPerBucket[k];
            }
        }
    }
    const reeksen = [];
    const jaarBars = [];
    for (const j of jaren) {
        const maanden = perJaar.get(j).map(sluitAf);
        reeksen.push({ key: String(j), label: String(j), periodes: maanden });
        // Eén staaf per jaar: de twaalf maanden opgeteld. `autos` wordt het hoogste
        // maandaantal, want dat is hoeveel auto's er dat jaar hebben gereden.
        const jaarBar = {
            key: String(j),
            label: String(j),
            posten: leegPosten(),
            variabelPerBucket: emptyBuckets(),
            totaal: 0,
            autos: 0,
            future: maanden.every((m) => m.future),
        };
        for (const m of maanden) {
            jaarBar.autos = Math.max(jaarBar.autos, m.autos);
            for (const k of Object.keys(jaarBar.posten))
                jaarBar.posten[k] += m.posten[k];
            for (const k of Object.keys(jaarBar.variabelPerBucket)) {
                jaarBar.variabelPerBucket[k] += m.variabelPerBucket[k];
            }
        }
        jaarBars.push(sluitAf(jaarBar));
    }
    // Nieuwste jaar vooraan, want daar kijk je meestal naar; de optelling erachter.
    reeksen.reverse();
    if (jaren.length > 1) {
        reeksen.push({ key: 'alle', label: 'Alle jaren', periodes: jaarBars });
    }
    return { reeksen, jaren };
}
//# sourceMappingURL=vloot-tco.js.map